import {
  addCommentEnvelope,
  changeDeclaredStatus,
  changeEnvelopeReviewer,
  changeTags,
  fetchEnvelopeThread,
  setComment,
  setCommentError,
} from 'actions';
import EnvelopeCollapsableSection from 'components/CollapsableSection/EnvelopeCollapsableSection';
import SelectRedesign from 'components/Select/SelectRedesign';
import { defaultReviewer } from 'constants/reviewSets';
import { capitalize } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Permissions from 'components/Permissions';
import { useParams } from 'react-router-dom';
import getSelectedEnvelope, {
  getBulkReviewStatus,
  getComment,
  getSelectedReviewer,
  getSelectedRule,
  getSelectedThreads,
} from 'selectors/envelopeReview';
import { getLastReview } from 'selectors/envelopes';
import { getEventsByEnvelopeId } from 'selectors/events';
import { getFurtherReviewTagGroup } from 'selectors/tagGroup';
import { useSelector } from 'store';
import { CommentWithUsers, EnvelopeAction } from 'types';
import logEvent from 'utils/analytics';
import CommentsSection from '../CommentsSection';
import EnvelopeSidebarAdvanced from './EnvelopeSidebarAdvanced';
import EnvelopeSidebarDetails from './EnvelopeSidebarDetails';
import InlineCommentSection from './InlineCommentSection';
import EnvelopeSidebarActions from './SidebarActions';

const EnvelopeSidebar: React.FC = () => {
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const dispatch = useDispatch();

  const [needFurtherReview, setNeedFurtherReview] = useState<string>('false');

  const events = useSelector(getEventsByEnvelopeId(envelopeId || ''));
  const rule = useSelector(getSelectedRule);
  const selectedReviewer = useSelector(getSelectedReviewer);
  const envelope = useSelector(getSelectedEnvelope);

  const furtherReviewTagGroup = useSelector(getFurtherReviewTagGroup);

  const isBulkReview = useSelector(getBulkReviewStatus);
  const selectedThreads = useSelector(getSelectedThreads);
  const lastReview = useSelector((state) => getLastReview(state, envelopeId));

  const comment = useSelector(getComment);

  const hideModelerEvents = selectedReviewer !== defaultReviewer.value;

  const commentsFilter = useCallback(
    (c: EnvelopeAction): boolean =>
      // @ts-ignore
      !c.payload?.lineIndex && c.payload?.lineIndex !== 0,
    []
  );

  useEffect(() => {
    if (envelope?.platform_thread_guid) {
      dispatch(fetchEnvelopeThread({ platformGuid: envelope.platform_thread_guid }));
    }
  }, [envelope?.platform_thread_guid, dispatch]);

  useEffect(() => {
    if (envelope && envelope.review_value) {
      if (lastReview && lastReview.review_value === envelope.review_value) {
        dispatch(changeDeclaredStatus({ status: lastReview.review_value }));
      } else {
        dispatch(changeDeclaredStatus({ status: envelope.review_value }));
      }
      dispatch(changeTags(envelope.tags || []));
    }

    if (furtherReviewTagGroup) {
      const tags = envelope?.tags || [];
      const hasFurtherReviewTag = tags.some((t) =>
        furtherReviewTagGroup.tag_values.some((tagValue) => tagValue.uuid === t.tag_value_uuid)
      );
      if (hasFurtherReviewTag) {
        setNeedFurtherReview('true');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelope?.uuid, dispatch]);

  useEffect(() => {
    if (furtherReviewTagGroup) {
      const tags = envelope?.tags || [];
      let hasFurtherReviewTag = false;

      hasFurtherReviewTag = tags.some((t) =>
        furtherReviewTagGroup.tag_values.some((tagValue) => tagValue.uuid === t.tag_value_uuid)
      );

      if (hasFurtherReviewTag) {
        setNeedFurtherReview('true');
      } else {
        setNeedFurtherReview('false');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelope, isBulkReview]);

  const event = events?.find((e) => e.rule_uuid === rule?.uuid);

  const reviewerOptions = [
    ...(furtherReviewTagGroup?.tag_values.map((tagValue) => ({
      value: tagValue.value,
      label: capitalize(tagValue.value),
    })) ?? []),
    defaultReviewer,
  ];

  const handleReviewerChange = (value: string): void => {
    dispatch(changeEnvelopeReviewer({ reviewer: value }));
  };

  const actionsSectionAction = (): JSX.Element | null => {
    if (furtherReviewTagGroup?.tag_values && furtherReviewTagGroup?.tag_values.length > 0) {
      return (
        <div className="w-full">
          <SelectRedesign
            onChange={handleReviewerChange}
            options={reviewerOptions}
            value={selectedReviewer || defaultReviewer.value}
            className="bg-litlingo-white"
          />
        </div>
      );
    }
    return null;
  };

  const getSidebarActionsTitle = (): string => {
    if (
      isBulkReview &&
      furtherReviewTagGroup?.tag_values &&
      furtherReviewTagGroup?.tag_values.length > 0
    ) {
      return `Bulk Review (${selectedThreads.length})`;
    }
    if (furtherReviewTagGroup?.tag_values && furtherReviewTagGroup?.tag_values.length > 0) {
      return 'Review as';
    }
    if (isBulkReview) {
      return `Bulk Review (${selectedThreads.length})`;
    }
    return 'Review';
  };

  const handleSaveComment = (value?: string): void => {
    logEvent('envelope-add-comment');

    const val = value || comment.value;

    dispatch(
      addCommentEnvelope({
        envelopeId,
        value: val,
        taggedUsers: comment.taggedUsers,
        textValue: comment.text,
      })
    );

    dispatch(setCommentError(false));
  };

  const handleSetComment = (value: CommentWithUsers): void => {
    dispatch(setComment(value));
  };

  return (
    <>
      <InlineCommentSection />
      <EnvelopeCollapsableSection
        headerTitle={getSidebarActionsTitle()}
        extraAction={actionsSectionAction()}
        bulkReview={isBulkReview}
      >
        <EnvelopeSidebarActions
          eventId={event?.uuid || ''}
          needFurtherReview={needFurtherReview}
          setNeedFurtherReview={setNeedFurtherReview}
        />
      </EnvelopeCollapsableSection>
      {!isBulkReview && (
        <EnvelopeCollapsableSection headerTitle="Comments">
          <div className="pt-4 bg-white">
            <CommentsSection
              setComment={handleSetComment}
              comment={comment}
              handleSaveComment={handleSaveComment}
              commentsFilter={commentsFilter}
              scrollOnError
            />
          </div>
        </EnvelopeCollapsableSection>
      )}

      {!isBulkReview && !hideModelerEvents && (
        <EnvelopeCollapsableSection headerTitle="Review History">
          <EnvelopeSidebarDetails envelopeId={envelopeId || ''} />
        </EnvelopeCollapsableSection>
      )}
      {!isBulkReview && (
        <Permissions action="communication_envelopes.advanced">
          <EnvelopeCollapsableSection headerTitle="Advanced Options">
            <EnvelopeSidebarAdvanced />
          </EnvelopeCollapsableSection>
        </Permissions>
      )}
    </>
  );
};

export default EnvelopeSidebar;
